import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../common/Typography';
import RankMovementLabel from '../leaderboard/RankMovementLabel';

export function getClubRoleBadgeClass(role) {
  switch (role) {
    case 'owner':
      return 'bg-primary/15 text-primary border border-primary/25';
    case 'admin':
      return 'bg-purple-500/15 text-purple-600 dark:text-purple-300 border border-purple-400/35';
    case 'host':
      return 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-400/30';
    default:
      return 'bg-surface-subtle text-text-secondary border border-border-medium';
  }
}

export function getClubRoleLabel(role) {
  if (!role) return '';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function getWinRateTone(winPct) {
  if (winPct == null) return 'none';
  if (winPct > 50) return 'above';
  if (winPct < 50) return 'below';
  return 'even';
}

export function getWinRateBadgeClass(winPct) {
  const tone = getWinRateTone(winPct);
  if (tone === 'above') {
    return 'border-emerald-500/30 bg-emerald-500/[0.12] dark:bg-emerald-500/15 dark:border-emerald-400/25';
  }
  // Below 50% — neutral grey (not red) so club record doesn't feel punitive
  if (tone === 'below' || tone === 'even') {
    return 'border-border-medium bg-surface-subtle dark:bg-surface-elevated/40';
  }
  return 'border-border-medium bg-surface-subtle dark:bg-surface-elevated/40';
}

export function getWinRateValueClass(winPct) {
  const tone = getWinRateTone(winPct);
  if (tone === 'above') return 'text-emerald-800 dark:text-emerald-100';
  if (tone === 'below') return 'text-text-muted';
  if (tone === 'even') return 'text-text-secondary';
  return 'text-text-muted';
}

function InsightsSkeleton({ layout }) {
  if (layout === 'compact') {
    return <div className="h-9 rounded-lg bg-surface-subtle animate-pulse" />;
  }
  return (
    <div className="space-y-3">
      <div className="h-11 rounded-xl bg-surface-subtle animate-pulse" />
      <div className="h-20 rounded-lg bg-surface-subtle animate-pulse" />
    </div>
  );
}

/**
 * Win rate + optional leaderboard rank block.
 * `layout="compact"` — single dense row for clubs page cards.
 * `layout="strip"` — taller block for homepage carousel cards.
 */
export function ClubPersonalInsights({
  clubId,
  clubWinRatePct = null,
  leaderboardTeaser = null,
  showLeaderboardLink = true,
  layout = 'strip',
  loading = false,
  className = '',
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className={className}>
        <InsightsSkeleton layout={layout} />
      </div>
    );
  }

  const teaser = leaderboardTeaser ?? null;
  const mixedEntry = teaser?.mixed || null;
  const levelsEntry = teaser?.levels || null;
  const levelsLabel =
    teaser?.levelsLadder === 'levels_male' ? "Men's Doubles" :
    teaser?.levelsLadder === 'levels_female' ? "Women's Doubles" :
    null;
  const compactLevelsLabel =
    teaser?.levelsLadder === 'levels_male' ? "Men's" :
    teaser?.levelsLadder === 'levels_female' ? "Women's" :
    null;
  const hasTeaser = mixedEntry || levelsEntry;

  const handleLeaderboardClick = (e) => {
    e.stopPropagation();
    const qs = new URLSearchParams({ tab: 'leaderboard' });
    if (teaser?.seasonId) qs.set('season', teaser.seasonId);
    navigate(`/clubs/${clubId}?${qs.toString()}`);
  };

  if (layout === 'compact') {
    return (
      <div className={className}>
        <div
          className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${getWinRateBadgeClass(clubWinRatePct)}`}
          title={
            clubWinRatePct != null
              ? 'Your mixed and levels win rate at this club'
              : 'Play recorded games here to build a record'
          }
        >
          <span className="text-[0.65rem] font-bold uppercase tracking-wide text-text-secondary">
            Win rate
          </span>
          <span className={`text-base font-bold tabular-nums ${getWinRateValueClass(clubWinRatePct)}`}>
            {clubWinRatePct != null ? `${clubWinRatePct}%` : '—'}
          </span>
        </div>
        {hasTeaser ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
            {mixedEntry ? (
              <span className="inline-flex items-center gap-1">
                Mixed <span className="font-bold text-text-primary">#{mixedEntry.rank}</span>
                <RankMovementLabel rankDelta={mixedEntry.rank_delta} size="xs" />
              </span>
            ) : null}
            {levelsEntry && compactLevelsLabel ? (
              <span className="inline-flex items-center gap-1">
                {compactLevelsLabel}{' '}
                <span className="font-bold text-text-primary">#{levelsEntry.rank}</span>
                <RankMovementLabel rankDelta={levelsEntry.rank_delta} size="xs" />
              </span>
            ) : null}
          </div>
        ) : (
          <Typography variant="caption" className="mt-2 block text-text-muted text-xs">
            Open club for leaderboard rankings
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className={`mb-2 sm:mb-3 rounded-xl border px-3 py-2 sm:py-2.5 shadow-sm ring-1 ring-black/[0.02] dark:ring-white/[0.04] ${getWinRateBadgeClass(clubWinRatePct)}`}
        title={
          clubWinRatePct != null
            ? 'Your mixed and levels win rate at this club'
            : 'Play ranked or social recorded games here to build a record'
        }
      >
        <div className="flex items-center justify-between gap-2">
          <Typography
            variant="caption"
            className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-text-secondary whitespace-nowrap"
          >
            Club record
          </Typography>
          <span className={`text-lg font-bold tabular-nums leading-none ${getWinRateValueClass(clubWinRatePct)}`}>
            {clubWinRatePct != null ? `${clubWinRatePct}%` : '—'}
          </span>
        </div>
      </div>

      <div className={`flex flex-col border-t border-border-subtle pt-2 sm:pt-3 ${showLeaderboardLink ? 'sm:min-h-[10rem]' : ''}`}>
        <Typography variant="caption" className="text-[0.65rem] font-semibold uppercase tracking-wide text-text-muted block mb-1 sm:mb-1.5 shrink-0">
          Your ranking
        </Typography>
        <div className={`flex flex-col gap-1 sm:gap-1.5 sm:min-h-[6.25rem] ${hasTeaser ? '' : 'sm:justify-center'}`}>
          {hasTeaser ? (
            <>
              {mixedEntry ? (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Mixed</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-text-primary">#{mixedEntry.rank}</span>
                    <RankMovementLabel rankDelta={mixedEntry.rank_delta} size="xs" />
                  </div>
                </div>
              ) : null}
              {levelsEntry && levelsLabel ? (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">{levelsLabel}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-text-primary">#{levelsEntry.rank}</span>
                    <RankMovementLabel rankDelta={levelsEntry.rank_delta} size="xs" />
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <Typography variant="caption" className="text-text-muted text-xs leading-relaxed pr-6">
              Play a ranked session to see your ranking
            </Typography>
          )}
        </div>
        {showLeaderboardLink ? (
          <button
            type="button"
            className="mt-1.5 sm:mt-auto w-full shrink-0 text-center text-xs font-semibold text-primary hover:underline pt-0.5 sm:pt-3 pb-0.5"
            onClick={handleLeaderboardClick}
          >
            View leaderboard →
          </button>
        ) : null}
      </div>
    </div>
  );
}
